import type { JSX } from "react";
import { Icon } from "./Icon";
import type { IconName } from "./Icon";
import { SidebarSection } from "./SidebarSection";
import { SidebarItem } from "./SidebarItem";
import { Stack } from "./Stack";

/* ── Local types (no dependency on mock-server) ─────────────── */

export interface SidebarSectionItemData {
  readonly icon: IconName;
  readonly title: string;
  readonly subtitles: readonly string[];
  readonly actionLabel?: string;
  readonly tone?: "default" | "highlight" | "muted";
}

export interface SidebarSectionData {
  readonly title: string;
  readonly pill?: string;
  readonly items: readonly SidebarSectionItemData[];
}

/* ── Component ──────────────────────────────────────────────── */

export interface SidebarSectionListProps {
  readonly sections: readonly SidebarSectionData[];
}

/**
 * Renders a list of sidebar sections with items.
 * Shared across Inbox, Emails, Meetings, and Companies sidebar views.
 */
export function SidebarSectionList({ sections }: SidebarSectionListProps): JSX.Element {
  return (
    <Stack gap={6} p={4}>
      {sections.map((section) => (
        <SidebarSection key={section.title} title={section.title} pill={section.pill}>
          {section.items.map((item) => (
            <SidebarItem
              key={item.title}
              icon={<Icon name={item.icon} size={13} />}
              title={item.title}
              subtitles={item.subtitles}
              actionLabel={item.actionLabel}
              tone={item.tone}
            />
          ))}
        </SidebarSection>
      ))}
    </Stack>
  );
}
